import React, { useEffect, useRef, useState } from 'react'
import { ImageOff } from 'lucide-react'
import { db } from '../../lib/db'
import { communityMediaRequestUrl } from '../../lib/communityData'

interface CommunityMediaProps {
  postId: string
  mediaType: 'photo' | 'video' | null
  className?: string
}

/**
 * Displays a community post's photo/video by fetching it through the
 * authenticated /api/community/media proxy (never a raw InstantDB signed
 * URL) and rendering the bytes as a local, in-memory blob URL — the exact
 * same pattern as AuthedImage for progress photos, and for the same
 * reason: a stored file URL is durable and fetchable by anyone who has it,
 * which is precisely the bug that got the old chat feature pulled.
 *
 * On any failure (not signed in, not eligible, post removed, etc.) this
 * renders a neutral placeholder — never a fallback to a raw storage URL.
 */
const CommunityMedia: React.FC<CommunityMediaProps> = ({ postId, mediaType, className }) => {
  const { user, isLoading: authLoading } = db.useAuth()
  const token = (user as any)?.refresh_token

  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  const objectUrlRef = useRef<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setBlobUrl(null)
    setFailed(false)

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = null
    }

    if (authLoading) return
    if (!token || !postId) {
      setFailed(true)
      return
    }

    ;(async () => {
      try {
        const res = await fetch(communityMediaRequestUrl(postId), {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        })
        if (!res.ok) {
          if (!cancelled) setFailed(true)
          return
        }
        const blob = await res.blob()
        if (cancelled) return
        const url = URL.createObjectURL(blob)
        objectUrlRef.current = url
        setBlobUrl(url)
      } catch (err) {
        console.error('CommunityMedia: failed to load media:', err)
        if (!cancelled) setFailed(true)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [postId, token, authLoading])

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
    }
  }, [])

  if (failed || !blobUrl) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 text-gray-400 ${className || ''}`}>
        <ImageOff size={20} />
      </div>
    )
  }

  if (mediaType === 'video') {
    return <video src={blobUrl} controls className={className} />
  }

  return <img src={blobUrl} alt="Community post" className={className} />
}

export default CommunityMedia
